/**
 * CLIENT-SESSION PAYLOAD ALLOW-LISTS.
 *
 * OWNER: app-architect. Read-only to everyone else; changes are arbitrated.
 *
 * MERCHANDISING_SPEC O-7 and OPS_SPEC §5.4 exclude internal fields from every
 * client-facing payload. This module makes that structural: a client payload is
 * BUILT by picking from an allow-list, never by deleting from a staff object.
 * Omission-discipline fails the first time somebody adds a column; an allow-list
 * fails closed.
 *
 * `assertNoInternalFields` is the second belt: it walks the serialised value and
 * throws if a forbidden key appears at ANY depth. Wire it into the client route
 * layer and into tests/security/client-payload-allowlist.test.ts.
 */

/**
 * Keys that may never appear in a client-session payload, at any depth.
 *  - lead times of any kind: D-026 (a) bans a delivery date or lead-time range
 *    in the client UI; O-7 makes `pricing.leadTimeDays` and `millLeadTimeDays`
 *    internal; OPS_SPEC §5.6 removed the mill re-order lead time from the client.
 *  - stock internals: OPS_SPEC §5.4 marks `onHandM` / `reservedM` staff-only.
 *  - ranking inputs: MERCHANDISING M-8a — `rankingSeason` is "derived per
 *    request, never rendered", excluded from every client payload, string and
 *    analytics event.
 *  - workshop identifiers and the internal halt reason: FLOWS_SPEC §8.1.
 */
export const FORBIDDEN_IN_CLIENT_PAYLOAD: ReadonlySet<string> = new Set([
  'leadTimeDays',
  'reorderLeadTimeDays',
  'millLeadTimeDays',
  'estimatedDeliveryDate',
  'estimatedWeeks',
  'rankingSeason',
  'rankingScore',
  'onHandM',
  'reservedM',
  'lowStockThresholdM',
  'boltId',
  'millLotRef',
  'actualMetreageM',
  'haltCode',
  'haltReason',
  'internalReason',
  'internalNote',
  'depositPercent',
  'depositMinor',
  'paymentSchedule',
  'refundPolicy',
  'costOwner',
]);

/** Cloth fields a client may see. Judge item 8 requires the first nine. */
export const CLIENT_CLOTH_FIELDS = [
  'id',
  'name',
  'mill',
  'composition',
  'weight_gsm',
  'width_cm',
  'weave',
  'pattern',
  'patternRepeatCm',
  'colourName',
  'colourSrgbHex',
  // CONDITIONALLY PRESENT. An allow-list says a field MAY appear, never that it
  // must. The projection omits `deltaE2000Tolerance` for every placeholder
  // cloth (all of them in Round 1) because a tolerance is a claim about a
  // measurement not made ([T-39]). `ClientColourHonesty.deltaE2000Tolerance` is
  // optional at the type level for the same reason — a consumer must handle its
  // absence, never default it.
  'deltaE2000Tolerance',
  'colourConfidence',
  'placeholder',
  'seasonClass',
  'occasions',
  'garments',
  'tags',
  // AMENDED 5 September 2026 (C1). `note` is the ENGINEER'S note on the rules
  // record ("Order as commission.type = dinner_jacket_only") and it was being
  // picked into the client payload and rendered on the cloth's own page as
  // the house note. It is replaced here by `clientNote`, the note written FOR
  // the client; `note` is no longer in the list, so it cannot reach a client
  // by any projection built from it. No cloth carries a `clientNote` yet.
  'clientNote',
  'lifecycleState',
  // Server-computed, per (cloth, configuration). OPS_SPEC §5.4.
  'availableMetres',
  'clothRequirementMetres',
  'stockBand',
  'selectable',
  'reasonCode',
  'tier',
  'tierReason',
  'pricePerMetreMinor',
  'totalPriceForCommissionMinor',
  'currency',
] as const;

/**
 * Order fields a client may see. FLOWS_SPEC §8.1: the projection "must NOT carry
 * metreage, bolt, internal halt reason, halt code, or any measurement value".
 */
export const CLIENT_ORDER_FIELDS = [
  'orderRef',
  'state',
  'placedAt',
  'commissionTypeLabel',
  'occasionLabel',
  'clothId',
  'clothName',
  'specification',
  'priceMinor',
  'currency',
  'promisedWindow',
  'daysInState',
  'stageHistory',
  'clientVisibleNotes',
  'acknowledgements',
  'measurementRecordId',
  'measurementTakenAt',
  'measurementAuthorName',
  'askActionsAvailable',
  'deliveredAt',
  'deliveryMethod',
  'cancelledAt',
  'cancelledReason',
] as const;

/** Measurement record fields a client may see — his own record, in full. */
export const CLIENT_MEASUREMENT_FIELDS = [
  'recordId',
  'status',
  'authoredByName',
  'authoredAt',
  'takenAt',
  'takenAtPlace',
  'method',
  'provenance',
  // `verifiedBy` is the GATE, not the label. FLOWS_SPEC §4.1 `ST-MEAS-LEGACY`
  // fires on `provenance = legacy_import` AND `verifiedBy = null`, so without
  // this key the field is absent rather than null and the state can never
  // render on any platform. `verifiedByName` is not a substitute: a name may be
  // absent for a verifier who is present. It is the provenance of the client's
  // OWN record — his to see, and none of the O-7 internal classes.
  'verifiedBy',
  'verifiedByName',
  'supersedes',
  'supersededBy',
  'reason',
  'values',
  'notes',
  'lastOpenedByName',
  'lastOpenedAt',
] as const;

export type AllowList = readonly string[];

/**
 * The mill name a CLIENT may be shown, or `null` for "name no mill".
 *
 * Added 5 September 2026 (C1). The placeholder catalogue carries
 * `mill: "House selection (placeholder)"` — a stand-in the house wrote for
 * itself, not the name of a mill — and it was reaching a client's page as
 * "Woven by House selection (placeholder)". The projection and the web
 * adapter BOTH pass the name through this one function, so the rule has one
 * home: a blank name, or any name that says "placeholder" in any case, is no
 * mill name at all. The cloth is then shown without a mill line rather than
 * with a made-up one. A real mill name passes through untouched.
 */
export function clientMillName(mill: unknown): string | null {
  if (typeof mill !== 'string') return null;
  const trimmed = mill.trim();
  if (trimmed === '' || /placeholder/i.test(trimmed)) return null;
  return trimmed;
}

/** Build a payload by picking. Missing keys are omitted, never nulled. */
export function pickAllowed<T extends Record<string, unknown>>(
  source: T,
  allow: AllowList,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of allow) {
    if (Object.prototype.hasOwnProperty.call(source, key)) out[key] = source[key];
  }
  return out;
}

/**
 * The tripwire. Throws if a forbidden key appears at any depth.
 *
 * Cheap enough to run on every client response in development and in tests; the
 * server wires it behind a flag that is ON in dev and in the test suite, so the
 * leak is found by us and not by a judge reading a network tab.
 */
export function assertNoInternalFields(value: unknown, path = '$'): void {
  if (value === null || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((v, i) => assertNoInternalFields(v, `${path}[${i}]`));
    return;
  }
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (FORBIDDEN_IN_CLIENT_PAYLOAD.has(key)) {
      throw new Error(
        `Client-payload allow-list breach at ${path}.${key} — "${key}" is internal-only ` +
          `(MERCHANDISING_SPEC O-7 / OPS_SPEC §5.4 / D-026(a)). Build the payload by ` +
          `picking from an allow-list in @pt/core/serialization, not by deleting keys.`,
      );
    }
    assertNoInternalFields(child, `${path}.${key}`);
  }
}
